import { useState, useEffect, useRef } from 'preact/hooks'
import { useTranslation } from 'react-i18next'
import { checkSignProcessStatus } from '@/services/api'
import { getConfig } from '../../config'
import { useNavigationGuard } from '@/services/navigation-guard'
import { advanceToStep } from '@/services/navigation'
import {
  type ProcessDtoLite,
  ProcessDtoLiteStatus,
} from '@cencert/mob-signature'
import { useLocation } from 'wouter-preact'
import { useConfig } from '@/services/config'
import { getProcessId, saveErrorMessage } from '@/services/session'

type StatusHandlerMode = 'qr-code' | 'waiting-for-signature'

interface UseSignProcessStatusPollingReturn {
  status: ProcessDtoLiteStatus | null
  isPolling: boolean
  error: string | null
  startPolling: () => void
  stopPolling: () => void
}

interface UseSignProcessStatusPollingOptions {
  mode?: StatusHandlerMode
}

export function useSignProcessStatusPolling(
  options: UseSignProcessStatusPollingOptions = {}
): UseSignProcessStatusPollingReturn {
  const { mode = 'qr-code' } = options
  const { t } = useTranslation()
  const { navigateTo } = useNavigationGuard()
  const [, setLocation] = useLocation()
  const { handleSessionExpired } = useConfig()

  const [status, setStatus] = useState<ProcessDtoLiteStatus | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const intervalRef = useRef<number | null>(null)
  const processIdRef = useRef<string | null>(null)

  // Initialize processId
  useEffect(() => {
    processIdRef.current = getProcessId()
  }, [])

  const stopPolling = () => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsPolling(false)
  }

  const handleQrCodeStatus = (processStatus: ProcessDtoLiteStatus) => {
    switch (processStatus) {
      case ProcessDtoLiteStatus.AUTHENTICATION_IN_PROGRESS:
        break

      case ProcessDtoLiteStatus.WAITING_FOR_SUBJECT_ACCEPTANCE:
        // Navigate to summary page for data confirmation
        stopPolling()

        if (advanceToStep('summary')) {
          navigateTo('/summary')
        }
        break

      case ProcessDtoLiteStatus.VERIFICATION_FAILED:
        stopPolling()
        setLocation('/process-error?errorType=verification-failed')
        break

      case ProcessDtoLiteStatus.PROCESS_ERROR:
        stopPolling()
        setLocation('/process-error?errorType=process-error')
        break

      case ProcessDtoLiteStatus.EXPIRED:
        stopPolling()
        handleSessionExpired()
        break

      case ProcessDtoLiteStatus.TERMINATED:
        stopPolling()
        setLocation('/process-error?errorType=terminated')
        break

      case ProcessDtoLiteStatus.ARCHIVED:
        stopPolling()
        setLocation('/process-error?errorType=archived')
        break

      case ProcessDtoLiteStatus.SIGNED:
        stopPolling()
        if (advanceToStep('waiting-for-signature')) {
          navigateTo('/waiting-for-signature')
        }
        break

      case ProcessDtoLiteStatus.PREPARING:
        break
      case ProcessDtoLiteStatus.SIGNING_IN_PROGRESS:
        if (advanceToStep('waiting-for-signature')) {
          navigateTo('/waiting-for-signature')
        }
        break
      default:
        stopPolling()
        setLocation('/process-error?errorType=invalid-status')
        // eslint-disable-next-line no-console
        console.warn(`Unexpected status during QR code phase: ${processStatus}`)
        break
    }
  }

  const handleWaitingForSignatureStatus = (
    processStatus: ProcessDtoLiteStatus
  ) => {
    switch (processStatus) {
      case ProcessDtoLiteStatus.SIGNING_IN_PROGRESS:
        break

      case ProcessDtoLiteStatus.SIGNED:
        stopPolling()
        break

      case ProcessDtoLiteStatus.EXPIRED:
        stopPolling()
        setLocation('/process-error?errorType=expired')
        break

      case ProcessDtoLiteStatus.PROCESS_ERROR:
        stopPolling()
        setLocation('/process-error?errorType=process-error')
        break

      case ProcessDtoLiteStatus.TERMINATED:
        stopPolling()
        setLocation('/process-error?errorType=terminated')
        break

      default:
        stopPolling()
        setLocation('/process-error?errorType=invalid-status')
        break
    }
  }

  const handleStatusResponse = (processData: ProcessDtoLite) => {
    const processStatus = processData.status
    setStatus(processStatus)
    setError(null)

    if (mode === 'qr-code') {
      handleQrCodeStatus(processStatus)
    } else if (mode === 'waiting-for-signature') {
      handleWaitingForSignatureStatus(processStatus)
    }
  }

  const pollStatus = async () => {
    if (!processIdRef.current) {
      setError('Brak identyfikatora procesu')
      stopPolling()
      return
    }

    await checkSignProcessStatus(processIdRef.current, {
      onError: error => {
        const errorMessage = error.response?.data?.detail
        setError(errorMessage ?? t('qrCode.errors.statusCheckFailed'))
        if (errorMessage) {
          saveErrorMessage(errorMessage)
        }
        setLocation('/process-error')
        stopPolling()
      },
      onSuccess: handleStatusResponse,
    })
  }

  const startPolling = () => {
    if (isPolling || !processIdRef.current) {
      return
    }

    setIsPolling(true)
    setError(null)

    const config = getConfig()
    const intervalMs = config.statusPollingIntervalMs ?? 5000

    // Initial check
    pollStatus()

    // Start periodic polling
    intervalRef.current = window.setInterval(pollStatus, intervalMs)
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [])

  return {
    status,
    isPolling,
    error,
    startPolling,
    stopPolling,
  }
}
